import styled, {css} from "styled-components";
import {useEffect, useMemo} from "react";
import {EnvironmentFilled} from "@ant-design/icons";
import {
  FaBehance,
  FaDiscord,
  FaFacebookF,
  FaGithub,
  FaInstagram,
  FaLinkedinIn,
  FaTiktok,
  FaTwitch,
  FaYoutube,
} from "react-icons/fa";
import {FaXTwitter} from "react-icons/fa6";
import {FiGlobe} from "react-icons/fi";
import {motion, useReducedMotion} from "framer-motion";
import {
  OfferingCollectionBlock,
  PUBLIC_PROFILE_THEME_BLOCK_COLOURS,
  PUBLIC_PROFILE_THEMES,
  PublicProfileBlock,
  PublicProfileDocument,
  PublicProfileSurfaceStyle,
  resolvePublicProfileThemeSettings,
} from "../domain";
import {OfferingOption, useGetOfferings} from "../../react-query-hooks/offerings";
import {useDigitalProducts} from "../../features/digital-products/hooks";
import {useProductServices} from "../../features/product-services/hooks";
import {useSessions} from "../../features/sessions/hooks";
import {useLiveEvents} from "../../features/live-events/hooks";
import {htmlToPlainText, sanitizeRichTextHtml} from "../../utils/html-to-plain-text";

const PUBLIC_PROFILE_FONT_STYLESHEET_ID = "public-profile-preview-fonts";

function googleFontStylesheetUrl(fonts: string[]) {
  const families = Array.from(new Set(fonts))
    .map((font) => `family=${font.replace(/ /g, "+")}:wght@400;500;600;700`)
    .join("&");
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

const PreviewPage = styled.div<{
  $mobilePreview: boolean;
  $background: string;
  $text: string;
  $muted: string;
  $headerSurface: string;
  $surface: string;
  $surfaceBorder: string;
  $surfaceRadius: number;
  $block: string;
  $blockBorder: string;
  $blockStyle: PublicProfileSurfaceStyle;
  $accent: string;
  $accentText: string;
  $buttonBorder: string;
  $buttonStyle: PublicProfileSurfaceStyle;
  $buttonRadius: number;
  $highlight: string;
  $highlightBorder: string;
  $highlightRadius: number;
  $headingFont: string;
  $bodyFont: string;
  $radius: number;
  $spacing: number;
}>`
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 3.2rem 2.4rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  background: ${({$background}) => $background};
  color: ${({$text}) => $text};
  font-family: ${({$bodyFont}) => `"${$bodyFont}", sans-serif`};
  scrollbar-color: #8f8c92 transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 0.4rem;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: #8f8c92;
  }

  button,
  a {
    font-family: inherit;
  }

  .profile-header {
    display: grid;
    justify-items: center;
    max-width: 68rem;
    margin: 0 auto ${({$spacing}) => `${$spacing}px`};
    overflow: hidden;
    border-radius: ${({$radius}) => `${$radius}px`};
    background: ${({$headerSurface}) => $headerSurface};
    text-align: center;
  }

  .profile-cover-wrap {
    position: relative;
    z-index: 1;
    width: calc(100% - 3.2rem);
    height: 17.4rem;
    margin: 1.6rem 1.6rem 0;
  }

  .profile-cover {
    width: 100%;
    height: 100%;
    border-radius: 1.8rem;
    object-fit: cover;
  }

  .cover-attribution {
    position: absolute;
    right: 0.8rem;
    bottom: 0.8rem;
    padding: 0.35rem 0.55rem;
    border-radius: 0.4rem;
    background: rgba(0, 0, 0, 0.62);
    color: #ffffff;
    font-size: 0.9rem;
  }

  .cover-attribution a {
    color: #ffffff;
    text-decoration: underline;
  }

  .profile-details {
    display: grid;
    justify-items: center;
    width: 100%;
    padding: 0 2.4rem 3.2rem;
  }

  .profile-header:not(.has-cover) .profile-details {
    padding-top: 3.2rem;
  }

  .profile-header:not(.has-cover) .profile-avatar {
    margin-top: 0;
  }

  .profile-avatar {
    position: relative;
    z-index: 2;
    width: 12.8rem;
    height: 12.8rem;
    margin-top: -6.4rem;
    margin-bottom: 1.2rem;
    border: 0.4rem solid ${({$headerSurface}) => $headerSurface};
    border-radius: 50%;
    object-fit: cover;
  }

  h1,
  h2,
  h3 {
    color: ${({$text}) => $text};
    font-family: ${({$headingFont}) => `"${$headingFont}", sans-serif`};
  }

  h1,
  h2,
  h3,
  p {
    margin-top: 0;
  }

  h1 {
    margin-bottom: 0.8rem;
    color: ${({$text}) => $text};
    font-size: 2.4rem;
    line-height: 1.25;
  }

  .muted {
    color: ${({$muted}) => $muted};
  }

  .profile-bio {
    max-width: 55rem;
    margin-bottom: 1rem;
    color: ${({$muted}) => $muted};
    font-size: 1.4rem;
    line-height: 1.55;
  }

  .profile-location {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1.2rem;
    color: ${({$muted}) => $muted};
    font-size: 1.3rem;
  }

  .profile-experience {
    margin: -0.2rem 0 1.2rem;
    padding: 0.5rem 0.9rem;
    border: 1px solid ${({$highlightBorder}) => $highlightBorder};
    border-radius: ${({$highlightRadius}) => `${$highlightRadius}px`};
    background: ${({$highlight}) => $highlight};
    color: ${({$muted}) => $muted};
    font-size: 1.2rem;
    font-weight: 600;
  }

  .profile-titles {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.6rem;
    margin-bottom: 1.2rem;
  }

  .profile-titles span {
    padding: 0.5rem 0.9rem;
    border: 1px solid ${({$highlightBorder}) => $highlightBorder};
    border-radius: ${({$highlightRadius}) => `${$highlightRadius}px`};
    background: ${({$highlight}) => $highlight};
    color: ${({$text}) => $text};
    font-size: 1.2rem;
    font-weight: 600;
  }

  .profile-header.basic-left .profile-titles {
    justify-content: flex-start;
  }

  .profile-socials {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
  }

  .profile-social {
    display: grid;
    width: 3.8rem;
    height: 3.8rem;
    place-items: center;
    border: 1px solid ${({$text}) => $text};
    border-radius: 0.9rem;
    background: ${({$text}) => $text};
    box-shadow: none;
    color: ${({$headerSurface}) => $headerSurface};
    font-size: 1.6rem;
    backdrop-filter: none;
  }

  .profile-header.basic-left,
  .profile-header.basic-left .profile-details {
    justify-items: start;
    text-align: left;
  }

  .profile-header.basic-left .profile-socials {
    justify-content: flex-start;
  }

  @media (max-width: 560px) {
    padding: 1.6rem 1.2rem;

    .profile-cover-wrap {
      width: calc(100% - 2.4rem);
      height: 13rem;
      margin: 1.2rem 1.2rem 0;
    }

    .profile-cover {
      border-radius: 1.4rem;
    }

    .profile-details {
      padding: 0 1.8rem 2.4rem;
    }

    .profile-avatar {
      width: 10.4rem;
      height: 10.4rem;
      margin-top: -5.2rem;
    }
  }

  .blocks {
    display: grid;
    gap: ${({$spacing}) => `${$spacing}px`};
    max-width: 68rem;
    margin: auto;
    transition: gap 150ms ease;
  }

  .preview-block {
    padding: 1.8rem;
    border: 1px solid ${({$blockBorder}) => $blockBorder};
    border-radius: ${({$radius}) => `${$radius}px`};
    background: ${({$block, $blockStyle}) =>
      $blockStyle === "outlined"
        ? "transparent"
        : $blockStyle === "glass"
        ? `radial-gradient(circle at 14% 0%, rgba(255, 255, 255, 0.72), transparent 36%), linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.06) 52%, rgba(255, 255, 255, 0.24)), color-mix(in srgb, ${$block} 58%, transparent)`
        : $block};
    box-shadow: ${({$blockStyle, $blockBorder}) =>
      $blockStyle === "hard-shadow"
        ? `6px 6px 0 ${$blockBorder}`
        : $blockStyle === "glass"
        ? "inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 12px 32px rgba(0, 0, 0, 0.08)"
        : "none"};
    color: ${({$text}) => $text};
    backdrop-filter: ${({$blockStyle}) =>
      $blockStyle === "glass" ? "blur(18px) saturate(170%)" : "none"};
    -webkit-backdrop-filter: ${({$blockStyle}) =>
      $blockStyle === "glass" ? "blur(18px) saturate(170%)" : "none"};
  }

  .preview-block > h2 {
    margin-bottom: 0.8rem;
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .preview-block > .block-description {
    margin-bottom: 2.4rem;
    color: ${({$muted}) => $muted};
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.5;
  }

  .rich-text-content {
    color: ${({$text}) => $text};
    font-size: 1.4rem;
    line-height: 1.6;
  }

  .rich-text-content > :last-child {
    margin-bottom: 0;
  }

  .rich-text-content a {
    color: ${({$accent}) => $accent};
    text-decoration: underline;
  }

  .rich-text-content .ql-align-center {
    text-align: center;
  }

  .rich-text-content .ql-align-right {
    text-align: right;
  }

  .preview-button,
  .preview-link {
    display: block;
    width: 100%;
    margin-top: 0.8rem;
    padding: 1rem 1.2rem;
    border: 1px solid ${({$buttonBorder}) => $buttonBorder};
    border-radius: ${({$buttonRadius}) => `${$buttonRadius}px`};
    background: ${({$accent, $buttonStyle}) =>
      $buttonStyle === "outlined"
        ? "transparent"
        : $buttonStyle === "glass"
        ? `color-mix(in srgb, ${$accent} 78%, transparent)`
        : $accent};
    box-shadow: ${({$buttonStyle}) =>
      $buttonStyle === "hard-shadow" ? "4px 4px 0 #111111" : "none"};
    color: ${({$accent, $accentText, $buttonStyle}) =>
      $buttonStyle === "outlined" ? $accent : $accentText};
    text-align: center;
    backdrop-filter: ${({$buttonStyle}) =>
      $buttonStyle === "glass" ? "blur(10px)" : "none"};
  }

  .item-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({$spacing}) => `${$spacing}px`};
    transition: gap 150ms ease;
  }

  .item-grid.item-list {
    grid-template-columns: minmax(0, 1fr);
  }

  ${({$mobilePreview}) =>
    $mobilePreview &&
    css`
      .item-grid.mobile-columns-1:not(.item-list) {
        grid-template-columns: minmax(0, 1fr);
      }

      .item-grid.mobile-columns-2:not(.item-list) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    `}

  @media (max-width: 560px) {
    .item-grid.mobile-columns-1:not(.item-list) {
      grid-template-columns: minmax(0, 1fr);
    }

    .item-grid.mobile-columns-2:not(.item-list) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .item-card {
    overflow: hidden;
    border: 1px solid ${({$surfaceBorder}) => $surfaceBorder};
    border-radius: ${({$surfaceRadius}) => `${$surfaceRadius}px`};
    background: ${({$surface}) => $surface};
    color: ${({$text}) => $text};
  }

  .offering-image-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: color-mix(in srgb, currentColor 8%, transparent);
  }

  .item-list .offering-image-wrap {
    aspect-ratio: 2.15 / 1;
  }

  .offering-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .offering-card-body {
    padding: 1.2rem;
  }

  .offering-card-title {
    display: block;
    margin-bottom: 0.8rem;
    color: ${({$text}) => $text};
    line-height: 1.35;
  }

  .offering-about {
    display: -webkit-box;
    margin: 0 0 1rem;
    overflow: hidden;
    color: ${({$muted}) => $muted};
    font-size: 1.3rem;
    font-weight: 400;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
  }

  .offering-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin: 0 0 1rem;
    font-size: 1.3rem;
    font-weight: 400;
  }

  .offering-meta span + span::before {
    margin-right: 0.6rem;
    content: "•";
  }

  .offering-cta {
    display: grid;
    width: 100%;
    min-height: 4.2rem;
    place-items: center;
    border: 1px solid ${({$buttonBorder}) => $buttonBorder};
    border-radius: ${({$buttonRadius}) => `${$buttonRadius}px`};
    background: ${({$accent, $buttonStyle}) =>
      $buttonStyle === "outlined"
        ? "transparent"
        : $buttonStyle === "glass"
        ? `color-mix(in srgb, ${$accent} 78%, transparent)`
        : $accent};
    box-shadow: ${({$buttonStyle}) =>
      $buttonStyle === "hard-shadow" ? "4px 4px 0 #111111" : "none"};
    color: ${({$accent, $accentText, $buttonStyle}) =>
      $buttonStyle === "outlined" ? $accent : $accentText};
    font-weight: 600;
    backdrop-filter: ${({$buttonStyle}) =>
      $buttonStyle === "glass" ? "blur(10px)" : "none"};
  }

  .preview-image {
    width: 100%;
    max-height: 28rem;
    border-radius: 1rem;
    object-fit: cover;
  }

  ${({$buttonStyle, $accent, $accentText, $buttonBorder}) =>
    $buttonStyle === "glass" &&
    css`
      .preview-button,
      .preview-link,
      .offering-cta {
        position: relative;
        isolation: isolate;
        overflow: hidden;
        border-color: ${$buttonBorder};
        background: radial-gradient(
            circle at 18% 0%,
            rgba(255, 255, 255, 0.8),
            transparent 38%
          ),
          linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.38),
            rgba(255, 255, 255, 0.08) 48%,
            rgba(255, 255, 255, 0.28)
          ),
          color-mix(in srgb, ${$accent} 32%, transparent);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86),
          inset 1px 0 0 rgba(255, 255, 255, 0.34), inset 0 -1px 0 rgba(255, 255, 255, 0.2),
          0 0.8rem 2.4rem color-mix(in srgb, ${$accent} 18%, transparent);
        color: ${$accentText};
        backdrop-filter: blur(18px) saturate(180%);
        -webkit-backdrop-filter: blur(18px) saturate(180%);
      }

      .preview-button::before,
      .preview-link::before,
      .offering-cta::before {
        position: absolute;
        inset: 1px;
        border-radius: inherit;
        background: linear-gradient(
          145deg,
          rgba(255, 255, 255, 0.48),
          transparent 38%,
          rgba(255, 255, 255, 0.12) 72%,
          rgba(255, 255, 255, 0.38)
        );
        content: "";
        pointer-events: none;
      }

      .preview-button::after,
      .preview-link::after,
      .offering-cta::after {
        position: absolute;
        top: -80%;
        bottom: -80%;
        left: -65%;
        width: 42%;
        transform: rotate(16deg);
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.54),
          transparent
        );
        content: "";
        pointer-events: none;
        animation: liquid-glass-sweep 5.5s ease-in-out infinite;
      }

      @keyframes liquid-glass-sweep {
        0%,
        62% {
          left: -65%;
          opacity: 0;
        }
        70% {
          opacity: 0.75;
        }
        92%,
        100% {
          left: 125%;
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .preview-button::after,
        .preview-link::after,
        .offering-cta::after {
          display: none;
        }
      }
    `}

  .profile-footer {
    display: grid;
    justify-items: center;
    gap: 1.6rem;
    max-width: 68rem;
    margin: ${({$spacing}) => `${Math.max($spacing, 24)}px`} auto 0;
    padding: 4.8rem 1.6rem;
    color: ${({$text}) => $text};
    font-size: 1.3rem;
    text-align: center;
  }

  .profile-footer p {
    margin-bottom: 0;
  }

  .coachli-branding {
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
  }

  .coachli-branding img {
    display: block;
    width: auto;
    height: 2.6rem;
    object-fit: contain;
  }

  .coachli-branding img.invert {
    filter: brightness(0) invert(1);
  }

  @media (min-width: 1600px) {
    padding: 1.6rem;

    .profile-header,
    .blocks {
      max-width: 96rem;
    }
  }
`;

function offeringCategoryMatches(
  collection: OfferingCollectionBlock["collection"],
  category: OfferingOption["category"]
) {
  if (collection === "services") return category === "services";
  if (collection === "liveEvents") return category === "liveEvents";
  if (collection === "store") {
    return category === "digitalProducts" || category === "productServices";
  }
  return true;
}

function offeringOptionKey(offering: OfferingOption): string {
  const kind =
    offering.category === "services"
      ? "service"
      : offering.category === "liveEvents"
      ? "live-event"
      : offering.category === "digitalProducts"
      ? "digital-product"
      : offering.category === "productServices"
      ? "product-service"
      : "subscription";
  return `${kind}:${offering.offeringId}`;
}

function formatOfferingPrice(currency: string | null, amount: number | null) {
  if (amount === null) return null;
  if (amount === 0) return "Free";
  const formattedAmount = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(amount);
  return currency ? `${currency.toUpperCase()} ${formattedAmount}` : formattedAmount;
}

function formatOfferingDate(date: string | null) {
  if (!date) return null;
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
}

function PreviewBlock({
  block,
  offerings,
}: {
  block: PublicProfileBlock;
  offerings: OfferingOption[];
}) {
  switch (block.type) {
    case "offeringCollection": {
      const selectedOfferingKeys = new Set(
        block.source.mode === "selected"
          ? block.source.offerings.map((offering) => `${offering.kind}:${offering.id}`)
          : []
      );
      const selectedOfferings = offerings
        .filter(
          (offering) =>
            offeringCategoryMatches(block.collection, offering.category) &&
            (block.source.mode === "automatic" ||
              selectedOfferingKeys.has(offeringOptionKey(offering)))
        )
        .slice(0, block.limit)
        .map((offering) => ({
          id: offering.offeringId,
          name: offering.offeringName,
          about: offering.description ? htmlToPlainText(offering.description) : null,
          imageUrl: offering.imageUrl,
          price: formatOfferingPrice(offering.currency, offering.amount),
          date: formatOfferingDate(offering.date),
          category: offering.category,
        }));
      const defaultButtonLabel =
        block.collection === "services"
          ? "Book now"
          : block.collection === "liveEvents"
          ? "Reserve a spot"
          : "Buy now";

      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          {block.description ? (
            <p className="muted block-description">{block.description}</p>
          ) : null}
          <div
            className={`item-grid mobile-columns-${block.mobileColumns ?? 1} ${
              block.layout === "list" ? "item-list" : ""
            }`}
          >
            {selectedOfferings.map((offering) => (
              <article className="item-card" key={`${offering.category}:${offering.id}`}>
                {offering.imageUrl ? (
                  <div className="offering-image-wrap">
                    <img className="offering-image" src={offering.imageUrl} alt="" />
                  </div>
                ) : null}
                <div className="offering-card-body">
                  <strong className="offering-card-title">{offering.name}</strong>
                  {block.collection === "services" &&
                  !offering.imageUrl &&
                  offering.about ? (
                    <p className="offering-about">{offering.about}</p>
                  ) : null}
                  {offering.price || offering.date ? (
                    <p className="offering-meta muted">
                      {offering.price ? <span>{offering.price}</span> : null}
                      {offering.date ? <span>{offering.date}</span> : null}
                    </p>
                  ) : null}
                  <button className="offering-cta" type="button">
                    {block.buttonLabel || defaultButtonLabel}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      );
    }
    case "customLink":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          {block.links.map((link, index) => (
            <div className="preview-link" key={`${link.label}-${index}`}>
              {link.label}
            </div>
          ))}
        </section>
      );
    case "socialLinks":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          <p className="muted">Instagram · LinkedIn · X · YouTube</p>
        </section>
      );
    case "richText":
      return (
        <section className="preview-block">
          {block.content.map((item, index) =>
            item.type === "heading" ? (
              <h2 key={index}>{item.text}</h2>
            ) : item.type === "bullet" ? (
              <p key={index}>• {item.text}</p>
            ) : (
              <div
                className="rich-text-content"
                // Content is restricted to the formats exposed by the text-block editor.
                dangerouslySetInnerHTML={{__html: sanitizeRichTextHtml(item.text)}}
                key={index}
              />
            )
          )}
        </section>
      );
    case "image":
      return (
        <section className="preview-block">
          <div className="item-card">Image preview</div>
          {block.caption ? <p className="muted">{block.caption}</p> : null}
        </section>
      );
    case "divider":
      return <hr style={{width: "100%", borderStyle: block.style}} />;
    case "video":
    case "embed":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          <div className="item-card">Embedded content preview</div>
        </section>
      );
    case "testimonials":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          {block.testimonials.map((item, index) => (
            <blockquote key={index}>
              “{item.quote}”
              <footer>
                — {item.author}
                {item.role ? `, ${item.role}` : ""}
              </footer>
            </blockquote>
          ))}
        </section>
      );
    case "countdown":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          <strong>07 days · 12 hours · 30 minutes</strong>
        </section>
      );
    case "emailSignup":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          <p className="muted block-description">{block.description}</p>
          <div className="item-card">Email address</div>
          <button className="preview-button" type="button">
            {block.buttonLabel}
          </button>
        </section>
      );
    case "faq":
      return (
        <section className="preview-block">
          <h2>{block.title}</h2>
          {block.items.map((item, index) => (
            <div key={index}>
              <strong>{item.question}</strong>
              <p className="muted">{item.answer}</p>
            </div>
          ))}
        </section>
      );
  }
}

export function LocalPublicProfilePreview({
  document,
  mobilePreview,
  displayName,
  username,
  profileImageUrl,
  coverImageUrl,
  coverImageAttribution,
  bio,
  location,
  professionalTitles,
  yearsExperience,
  socialLinks,
}: {
  document: PublicProfileDocument;
  mobilePreview: boolean;
  displayName: string;
  username: string;
  profileImageUrl: string;
  coverImageUrl: string | null;
  coverImageAttribution: {
    photographerName: string;
    photographerUrl: string;
  } | null;
  bio: string;
  location: string;
  professionalTitles: string[];
  yearsExperience: string;
  socialLinks: Array<{name: string; url: string}>;
}) {
  const offeringsQuery = useGetOfferings();
  const digitalProductsQuery = useDigitalProducts(1, 100);
  const productServicesQuery = useProductServices(1, 100);
  const sessionsQuery = useSessions({page: 1, perPage: 100});
  const liveEventsQuery = useLiveEvents({filter: "upcoming", page: 1, perPage: 100});
  const enrichedOfferings = useMemo(() => {
    const offeringDetails = new Map<
      string,
      Pick<OfferingOption, "description" | "imageUrl" | "currency" | "amount" | "date">
    >();

    digitalProductsQuery.data?.data.forEach((product) => {
      offeringDetails.set(`digitalProducts:${product.id}`, {
        description: null,
        imageUrl: product.imageUrl,
        currency: product.currency,
        amount: product.amount,
        date: null,
      });
    });
    productServicesQuery.data?.data.forEach((service) => {
      offeringDetails.set(`productServices:${service.id}`, {
        description: null,
        imageUrl: service.imageUrl,
        currency: service.currency,
        amount: service.amount,
        date: null,
      });
    });
    sessionsQuery.data?.data.forEach((session) => {
      const baseCurrency =
        session.currencies.find((currency) => currency.isAutoConverted === false) ??
        session.currencies.find(
          (currency) => currency.currency === session.callPriceCurrency
        );
      offeringDetails.set(`services:${session.id}`, {
        description: session.description,
        imageUrl: session.coverPicture || null,
        currency: baseCurrency?.currency ?? session.callPriceCurrency,
        amount: baseCurrency?.amount ?? session.callPrice,
        date: null,
      });
    });
    liveEventsQuery.data?.data.forEach((event) => {
      offeringDetails.set(`liveEvents:${event.id}`, {
        description: event.description,
        imageUrl: event.coverImage || null,
        currency: event.callPriceCurrency,
        amount: event.callPrice,
        date: event.eventDate,
      });
    });

    return (offeringsQuery.data ?? []).map((offering) => {
      const detail = offeringDetails.get(`${offering.category}:${offering.offeringId}`);
      return detail
        ? {
            ...offering,
            description: detail.description ?? offering.description,
            imageUrl: detail.imageUrl ?? offering.imageUrl,
            currency: detail.currency ?? offering.currency,
            amount: detail.amount ?? offering.amount,
            date: detail.date ?? offering.date,
          }
        : offering;
    });
  }, [
    digitalProductsQuery.data,
    liveEventsQuery.data,
    offeringsQuery.data,
    productServicesQuery.data,
    sessionsQuery.data,
  ]);
  const prefersReducedMotion = useReducedMotion();
  const settings = resolvePublicProfileThemeSettings(document.theme);
  const colours = document.theme.overrides;
  useEffect(() => {
    const href = googleFontStylesheetUrl([settings.headingFont, settings.bodyFont]);
    let stylesheet = window.document.querySelector<HTMLLinkElement>(
      `#${PUBLIC_PROFILE_FONT_STYLESHEET_ID}`
    );
    if (!stylesheet) {
      stylesheet = window.document.createElement("link");
      stylesheet.id = PUBLIC_PROFILE_FONT_STYLESHEET_ID;
      stylesheet.rel = "stylesheet";
      window.document.head.appendChild(stylesheet);
    }
    if (stylesheet.href !== href) stylesheet.href = href;
  }, [settings.bodyFont, settings.headingFont]);
  const preset = PUBLIC_PROFILE_THEMES.find(
    (theme) => theme.id === document.theme.preset
  )!;
  const isFrostTheme = document.theme.preset === "frost";
  const isLushTheme = document.theme.preset === "lush";
  const isMidnightTheme = document.theme.preset === "midnight";
  const previewColours = {
    background: preset.colours[0],
    surface: settings.surfaceBackground,
    text: preset.colours[2],
    muted: isMidnightTheme
      ? "#c4c4c4"
      : `color-mix(in srgb, ${preset.colours[2]} 76%, transparent)`,
    accent: isFrostTheme
      ? "#ffffff"
      : isLushTheme
      ? preset.colours[0]
      : preset.colours[2],
    accentText:
      isFrostTheme || isLushTheme
        ? preset.colours[2]
        : isMidnightTheme
        ? preset.colours[0]
        : "#ffffff",
  };
  const backgroundColour = colours.pageBackground ?? previewColours.background;
  const previewBackground =
    settings.backgroundStyle === "gradient"
      ? `linear-gradient(135deg, ${backgroundColour}, ${settings.backgroundGradientEnd})`
      : settings.backgroundStyle === "image" && settings.backgroundImageUrl
      ? `url("${settings.backgroundImageUrl.replace(
          /"/g,
          '\\"'
        )}") center / cover no-repeat`
      : backgroundColour;
  return (
    <PreviewPage
      $mobilePreview={mobilePreview}
      $background={previewBackground}
      $text={colours.text ?? previewColours.text}
      $muted={colours.text ?? colours.textMuted ?? previewColours.muted}
      $headerSurface={preset.colours[1]}
      $surface={colours.surface ?? previewColours.surface}
      $surfaceBorder={settings.surfaceBorderColor}
      $surfaceRadius={settings.surfaceRadius}
      $block={
        colours.surfaceMuted ?? PUBLIC_PROFILE_THEME_BLOCK_COLOURS[document.theme.preset]
      }
      $blockBorder={settings.blockBorderColor}
      $blockStyle={settings.blockStyle}
      $accent={colours.accent ?? previewColours.accent}
      $accentText={colours.accentText ?? previewColours.accentText}
      $buttonBorder={isFrostTheme ? "#9fafd0" : colours.accent ?? previewColours.accent}
      $buttonStyle={settings.buttonStyle}
      $buttonRadius={settings.buttonRadius}
      $highlight={settings.highlightBackground}
      $highlightBorder={settings.highlightBorderColor}
      $highlightRadius={settings.highlightRadius}
      $headingFont={settings.headingFont}
      $bodyFont={settings.bodyFont}
      $radius={settings.blockRadius}
      $spacing={settings.blockSpacing}
      data-testid="local-public-profile-preview"
    >
      <motion.header
        layout={!prefersReducedMotion}
        className={`profile-header ${document.header.layout} ${
          document.header.showCoverImage && coverImageUrl ? "has-cover" : ""
        }`}
        transition={{layout: {type: "spring", stiffness: 260, damping: 28}}}
      >
        {document.header.showCoverImage && coverImageUrl ? (
          <motion.div
            layout={!prefersReducedMotion}
            className="profile-cover-wrap"
            transition={{type: "spring", stiffness: 260, damping: 28}}
          >
            <img className="profile-cover" src={coverImageUrl} alt="" />
            {coverImageAttribution ? (
              <span className="cover-attribution">
                Photo by{" "}
                <a
                  href={coverImageAttribution.photographerUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {coverImageAttribution.photographerName}
                </a>{" "}
                on{" "}
                <a
                  href="https://unsplash.com/?utm_source=coachli&utm_medium=referral"
                  target="_blank"
                  rel="noreferrer"
                >
                  Unsplash
                </a>
              </span>
            ) : null}
          </motion.div>
        ) : null}
        <motion.div
          layout={!prefersReducedMotion}
          className="profile-details"
          transition={{type: "spring", stiffness: 240, damping: 28}}
        >
          {document.header.showAvatar ? (
            <motion.img
              layout={!prefersReducedMotion}
              className="profile-avatar"
              src={profileImageUrl}
              alt=""
              transition={{type: "spring", stiffness: 280, damping: 25}}
            />
          ) : null}
          <motion.h1 layout={!prefersReducedMotion}>{displayName}</motion.h1>
          {bio ? (
            <motion.p layout={!prefersReducedMotion} className="profile-bio">
              {bio}
            </motion.p>
          ) : null}
          {document.header.showLocation && location ? (
            <motion.span layout={!prefersReducedMotion} className="profile-location">
              <EnvironmentFilled aria-hidden="true" /> {location}
            </motion.span>
          ) : null}
          {professionalTitles.length ? (
            <motion.div
              layout={!prefersReducedMotion}
              className="profile-titles"
              aria-label="Professional titles"
            >
              {professionalTitles.map((title) => (
                <span key={title}>{title}</span>
              ))}
            </motion.div>
          ) : null}
          {yearsExperience.trim() ? (
            <motion.span layout={!prefersReducedMotion} className="profile-experience">
              {yearsExperience.trim()} years of experience
            </motion.span>
          ) : null}
          {socialLinks.length ? (
            <motion.div
              layout={!prefersReducedMotion}
              className="profile-socials"
              aria-label={`${displayName}'s social links`}
            >
              {socialLinks
                .slice(0, document.header.maximumSocialLinks)
                .map(({name, url}) => {
                  const Icon = SOCIAL_ICONS[name] ?? FiGlobe;
                  return (
                    <a
                      className="profile-social"
                      href={url}
                      aria-label={name}
                      title={name}
                      key={name}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon aria-hidden="true" />
                    </a>
                  );
                })}
            </motion.div>
          ) : null}
        </motion.div>
      </motion.header>
      <main className="blocks">
        {document.blocks
          .filter((block) => block.visible)
          .map((block) => (
            <PreviewBlock block={block} offerings={enrichedOfferings} key={block.id} />
          ))}
      </main>
      <footer className="profile-footer">
        <p>
          © {new Date().getFullYear()} {displayName}. All rights reserved
        </p>
        <div className="coachli-branding">
          <span>Powered by</span>
          <img
            className={isMidnightTheme ? "invert" : undefined}
            src="/images/coachli-new-logo.svg"
            alt="Coachli"
          />
        </div>
      </footer>
    </PreviewPage>
  );
}

const SOCIAL_ICONS: Record<string, typeof FaInstagram> = {
  behance: FaBehance,
  discord: FaDiscord,
  facebook: FaFacebookF,
  github: FaGithub,
  instagram: FaInstagram,
  linkedin: FaLinkedinIn,
  tiktok: FaTiktok,
  twitch: FaTwitch,
  twitter: FaXTwitter,
  website: FiGlobe,
  youtube: FaYoutube,
};
